import { GetServerSideProps } from 'next'
import pluralize from 'pluralize'
import { slugify } from 'utils/slug'
import api from 'services/api'
import Layout from 'components/layout'
import TitleBar from 'components/title-bar'
import Avatar from 'components/avatar'
import routes from 'routes'
import { NextSeo } from 'next-seo'
import Author from 'models/author'
import { getRedirectReturn } from 'utils/redirect'
import { HOST } from 'config'
import Keywords from 'components/keywords'
import Breadcrumb from 'components/breadcrumb'
import { Heading } from 'pages'
import TagList from 'components/tag-list'
import AppDownload from 'components/app-download'
import Tag from 'models/tag'
import BookViewComplex from 'components/book-view-complex'
import { withStorage } from 'utils/withFunction'
import JsonLd, { CommonJsonLd } from 'components/json-ld'

const AuthorPage = ({
  author,
  books,
  path,
  tags,
}: {
  author: Author
  books: any[]
  path: string
  tags: Tag[]
}): JSX.Element => {
  let title = author.name
  if (books.length) title = title + ` Author of ${books[0]['title']}`

  let description = ''

  if (books.length) {
    description = `${author.name} is the author of ${books
      .slice(0, 3)
      .map((book) => book?.title)
      .join(', ')}`
    if (books.length > 3)
      description =
        description + ` and ${pluralize('other story', books.length - 1, true)}`
    description =
      description +
      '. Read these stories and novels free online at AnyStories.app.'
  }

  return (
    <Layout isFullScreen={true}>
      <Keywords keywords={[author.name]} />
      <NextSeo
        title={title}
        description={description}
        canonical={`${HOST}${path}`}
        openGraph={{
          title,
          description,
          url: HOST + routes.author.details(author),
          type: 'profile',
          profile: {
            username: author.name,
          },
          images: [
            {
              url: author.avatar,
              alt: `Avatar for ${author.name}`,
            },
          ],
        }}
      />
      <CommonJsonLd />
      <JsonLd
        data={{
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              name: 'AnyStories',
              item: HOST,
            },
            {
              '@type': 'ListItem',
              position: 2,
              name: author.name,
              item: HOST + routes.author.details(author),
            },
          ],
        }}
      />
      <div className="bg-gradient-to-r from-[#f8efba] to-[#f7cac8]">
        <div className="max-w-[1440px] mx-auto">
          <TitleBar className="text-white" />
          <div className="hidden lg:block px-4 py-3">
            <Breadcrumb
              className="mr-4"
              items={[
                {
                  name: author.name,
                },
              ]}
            />
          </div>
          <div className="flex items-center pt-2 pb-4 px-4">
            {author.photo && (
              <div className="mr-3">
                <Avatar
                  src={author.photo}
                  name={author.name}
                  className="w-14 h-14"
                />
              </div>
            )}
            <div>
              <h1 className="font-bold">{author.name}</h1>
              {author.medals?.length > 0 && (
                <div>
                  <svg
                    xmlns="https://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    className="fill-current text-gold w-4 inline-block"
                  >
                    <path
                      fillRule="evenodd"
                      d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span className="text-xs inline-block ml-1">
                    {author.medals[0].name}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="max-w-[1440px] mx-auto">
        <section className="desktop:flex">
          <section className="desktop:w-[1018px] mb-4">
            <div className="p-4">
              <h2 className="font-bold text-xl mb-2">{`${pluralize(
                'Story',
                books.length,
                true
              )} of ${author.name}`}</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-[24px] py-[12px]">
                {books.map((item, index) => (
                  <BookViewComplex
                    book={item}
                    showIntro={true}
                    showTags={true}
                    key={index}
                  />
                ))}
              </div>
            </div>
          </section>
          <section className="desktop:w-[422px]">
            <section className="p-4 mb-4">
              <Heading title="Popular Tags" moreLink={routes.tag.list()} />
              <TagList tags={tags} />
            </section>
            <section className="px-4 mb-4">
              <AppDownload />
            </section>
            <div className="h-4"></div>
          </section>
        </section>
      </div>
    </Layout>
  )
}

export const getServerSideProps: GetServerSideProps = withStorage(
  async ({ context }) => {
    const id = context.params.id as string
    const slug = context.params.slug as string

    const [author, books] = await Promise.all([
      api.author.getAuthorInfo(id),
      api.author.getAuthorBookList({ id }),
    ])

    if (!author)
      return {
        redirect: {
          permanent: true,
          destination: `/search?keywords=${slug.replaceAll('-', '+')}`,
        },
      }

    const path = routes.author.details({ id, name: author.name })

    if (slugify(author.name) !== context.params.slug) {
      return getRedirectReturn(context.req.url, path)
    }

    const tags = await api.tag.getHomeTags()

    return {
      props: {
        author,
        books,
        path,
        tags,
      },
    }
  }
)

export default AuthorPage
